import { Component, Input, OnInit, ViewChild, Output, EventEmitter, OnChanges } from '@angular/core';
import { DxFormComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import { ToastrService } from 'ngx-toastr';
import { DxDataGridComponent, DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { SellerSnapshotField } from '../../models/seller-snapshot-field';
import { CoreFormItemTemplates, CoreInputEditorType } from '../../constants/dev-extreme-enums';
import { unwrap, unwrapArray } from '../../unwrap';
import { SellerSnapshotService } from '../../services/seller-snapshot.service';
import { Seller } from '../../models/seller';
import { HistSeller } from '../../models/hist-seller';
import { AccountFactor } from '../../models/account-factor';
import { CoreFeature, CoreFormItemType, SellerSnapshotFieldGroup, SellerSnapshotFieldType, SellerSnapshotDatasourceFieldType, CoreAccessLevel } from '../../constants/enums';
import { Permission } from '../../models/permission';
import { HelperService } from '../../services/helper.service';
import { SellerSnapshotChangesContext } from '../../models/contexts/seller-snapshot-changes-context';
import { HistSellerAuditResults } from '../../models/hist-seller-audit-results';
import { CoreResponse } from '../../models/core-response';
import { SellerLayout } from '../../models/seller-layout';
import { PermissionService } from '../../services/permission.service';
import { SellerSnapshotFormFieldProperties } from '../../models/seller-snapshot-form-field-properties';
import { SellerSnapshotDatasourceContext } from '../../models/contexts/seller-snapshot-datasource-context';
import { CoreColumn } from '../../models/core-column';
import { GridProps } from '../../models/core-data-grid-properties';
import { XactionService } from '../../services/xaction.service';
import { forkJoin } from 'rxjs';
import { ColumnType } from '../../models/core-column-type';
import { AppElementsService } from '../../services/app-element.service';
import { FieldService } from '../../services/field.service';
import { Singleton } from '../../models/singleton';
import { SellerSnapshotGridConfigPopupComponent } from '../seller-snapshot-grid-config-popup/seller-snapshot-grid-config-popup.component';

@Component({
    selector: 'app-seller-snapshot',
    templateUrl: './seller-snapshot.component.html',
    styleUrls: ['./seller-snapshot.component.scss']
})

export class SellerSnapshotComponent implements OnInit, OnChanges {
    @ViewChild('fieldGrid', {static: false})
    fieldGrid: DxDataGridComponent;
    @ViewChild('formGrid', {static: false})
    formGrid: DxDataGridComponent;
    @ViewChild('snapshotForm', {static: false})
    snapshotForm: DxFormComponent;
    @ViewChild('gridConfigPopup', {static: false})
    gridConfigPopup: SellerSnapshotGridConfigPopupComponent;

    @Input() date: Date;
    @Input() seller: Seller;
    @Input() histSellers: HistSeller[];
    @Input() accountFactors: AccountFactor[];
    @Input() attributeLookups: any;
    @Input() sellerMappings: any;
    @Input() permissions: Permission[];
    @Input() sellerLayout: SellerLayout;
    @Input() editTabOnlyMode: boolean;

    @Output() dataUpdated = new EventEmitter();
    @Output() updateFailed = new EventEmitter<CoreResponse<HistSellerAuditResults>>();
    @Output() layoutUpdated = new EventEmitter<number>();

    snapshotFields: SellerSnapshotField[];
    horizontalLineFieldKey: string = 'HL';
    uiSnapshotFields: SellerSnapshotField[] = [
        new SellerSnapshotField(this.horizontalLineFieldKey, '--- Horizontal Line ---', SellerSnapshotFieldType.UIElement, null, CoreFormItemTemplates.HorizontalLineTemplate)
    ];
    snapshotFormData: any;
    snapshotFormDataChanges: any;
    isSnapshotEditable: boolean = false;
    isDateUsed: boolean = false;
    snapshotRefreshing: boolean = false;
    pendingSnapshotRefresh: boolean = false;
    snapshotDatasources: { dataSource: any[], columns: CoreColumn[], props: GridProps }[] = [];
    masterMapping: any = {};
    columnMappings: any;
    singletons: Singleton[] = [];
    isTabChanging: boolean = false;
    pageLoaded: boolean = false;
    histSeller: HistSeller;
    editPopupVisible: boolean = false;
    popupHeight: number = 700;
    popupGridHeightDiff: number = 250;
    popupGridHeight: number;
    minPopupHeight: number = 500;
    formItems: SellerSnapshotFormFieldProperties[];
    cleanedFormItems: SellerSnapshotFormFieldProperties[];
    formColCount: number = 1;
    editLayoutPopupSaveButtonOptions: any;
    editLayoutPopupCancelButtonOptions: any;
    editLayoutPopupDeleteButtonOptions: any;
    editGridConfigPopupSaveButtonOptions: any;
    editGridConfigPopupCancelButtonOptions: any;
    groupItemCaption: string = '<Group>';
    horizontalLineTemplateName: string = CoreFormItemTemplates.HorizontalLineTemplate;
    datasourceGridTemplateName: string = CoreFormItemTemplates.DatasourceGridTemplate;
    labelTemplateName: string = CoreFormItemTemplates.LabelTemplate;
    editGridConfigPopupVisible: boolean = false;

    permissionEditSeller: boolean = false;
    permissionEditAccountHistory: boolean = false;    
    permissionAddFactor: boolean = false;
    permissionEditFactor: boolean = false;    
    permissionEditAccountSecurity: boolean = false;
    permissionDeleteAccountSnapshotLayouts: boolean = false;
    
    constructor(private sellerSnapshotService: SellerSnapshotService,
            private xactionService: XactionService,
            private fieldService: FieldService,
            private permissionService: PermissionService,
            private appElementsService: AppElementsService,
            private helperService: HelperService,
            private toast: ToastrService) {
        
    }

    ngOnInit() {
        this.permissionService.initializePermissions().then(_ => {
            this.permissionEditSeller = this.permissionService.checkCurrentUserPermission(CoreFeature.EditAccounts.toString());
            this.permissionEditAccountHistory = this.permissionService.checkCurrentUserPermission(CoreFeature.EditAccountHistory.toString());
            this.permissionEditAccountSecurity = this.permissionService.checkCurrentUserPermission(CoreFeature.EditAccountSecurity.toString());
            this.permissionAddFactor = this.permissionService.checkCurrentUserPermission(CoreFeature.AddAccountFactors.toString());
            this.permissionEditFactor = this.permissionService.checkCurrentUserPermission(CoreFeature.EditAccountFactors.toString());
            this.permissionDeleteAccountSnapshotLayouts = this.permissionService.checkCurrentUserPermission(CoreFeature.DeleteAccountSnapshotLayouts.toString());
        });
        
        if (!this.editTabOnlyMode) {
            this.sellerSnapshotService.getSellerSnapshotFields().pipe(unwrapArray(this.toast)).subscribe(fields => {
                this.snapshotFields = fields.sort((a, b) => a.caption.localeCompare(b.caption));
                this.snapshotFields.push(...this.uiSnapshotFields);
                this.formItems = [];
                this.snapshotFields.forEach(field => {
                    field.group = this.getItemGroup(field)
                });
            });

            forkJoin([
                this.xactionService.getXactionMappings(),
                this.xactionService.getAllXactionColumns(false, true),
                this.fieldService.getSingletons()
            ]).subscribe(([mappings, columnMappings, singletons]) => {
                this.masterMapping = mappings;
                this.columnMappings = columnMappings;
                this.singletons = singletons;

                if (this.pendingSnapshotRefresh) {
                    this.pendingSnapshotRefresh = false;
                    this.refreshSnapshot();
                }
            });
        }
    }

    ngOnChanges(e: any): void {
        // This is to avoid incorrect message from visibly displaying for split second when the user selects new seller
        if (e.seller) {
            this.snapshotRefreshing = true;
            this.histSeller = null;
        }
    }

    refreshSnapshot(): void {
        let isFormItemsConstructed = false;
        if (!this.editTabOnlyMode && this.seller) {
            if (Object.keys(this.masterMapping).length === 0) {
                this.pendingSnapshotRefresh = true;
            } else {
                this.getCurrentHistSeller();
                this.snapshotFormData = {};
                this.snapshotFormDataChanges = {};
    
                if (this.sellerLayout.id > 0 && !this.sellerLayout?.isGridView && this.snapshotFields) {
                    const layout = JSON.parse(this.sellerLayout.layout);
                    this.formColCount = layout.formColCount ?? 0;
                    this.formItems = [];
                    layout.formItems.forEach(persistedFormItem => this.formItems.push(this.getFullFormItem(persistedFormItem)));
                    if (this.histSeller || !this.isSnapshotDateUsed()) {
                        isFormItemsConstructed = true;
                        this.snapshotFields.forEach(field => field.visible = !this.formItems.some(x => x.dataField === field.fieldKey || x.items?.some(y => y.dataField === field.fieldKey))
                            || field.type === SellerSnapshotFieldType.UIElement);
                    }
    
                } else {
                    this.formColCount = 1;
                    this.formItems = [];
                }

                this.formGrid.instance.repaint();
                setTimeout(() => this.snapshotRefreshing = false);
                this.formItemsChanged(!isFormItemsConstructed);
                this.isTabChanging = false;
            }
        }
    }

    onEditGridConfig = (e): void => {        
        this.gridConfigPopup.fieldProps = e.row.data;
        this.editGridConfigPopupVisible = true;  
        this.gridConfigPopup.editGridConfig();
    }

    saveEditGridConfigPopup(): void {
        const selectedColumns = [];
        this.gridConfigPopup.gridColumns.forEach(x => {
            if (this.gridConfigPopup.fieldProps.gridColumns?.indexOf(x.column) > -1) {
                selectedColumns.push(x.column);
            }
        });
        const formItem = this.getFormItemByFieldName(this.gridConfigPopup.fieldProps.dataField);
        formItem.colSpan = this.gridConfigPopup.fieldProps.colSpan;
        formItem.heightValue = this.gridConfigPopup.fieldProps.heightValue;
        formItem.gridColumns = selectedColumns;
        formItem.gridFilterObject = this.gridConfigPopup.filterBuilderValue;

        this.formItemsChanged();
        this.closeEditGridConfigPopup();
    }

    closeEditGridConfigPopup(): void {
        this.editGridConfigPopupVisible = false;
        this.gridConfigPopup.gridColumnsGrid.instance.clearFilter();
    }

    getFormItemByFieldName(dataField: string): SellerSnapshotFormFieldProperties {
        let formItem = null;
        this.formItems.forEach(item => {
            if (item.dataField === dataField) {
                formItem = item;
            } else if (item.items && item.items.length > 0) {
                item.items.forEach(childItem => {
                    if (childItem.dataField === dataField) {
                        formItem = childItem;
                    }
                })
            }
        });
        return formItem;
    }

    getFullFormItem(persistedFormItem: SellerSnapshotFormFieldProperties): SellerSnapshotFormFieldProperties {
        if (persistedFormItem.itemType === CoreFormItemType.Simple) {
            const snapshotField = this.snapshotFields.find(x => x.fieldKey === persistedFormItem.dataField);
            if (snapshotField.type === SellerSnapshotFieldType.Datasource) {
                this.configureDatasourceGrid(persistedFormItem);
                return new SellerSnapshotFormFieldProperties().createSimpleItem(snapshotField.fieldKey, snapshotField.caption, null, null, CoreInputEditorType.Grid, 
                    persistedFormItem.colSpan, null, null, null, persistedFormItem.heightValue, persistedFormItem.gridColumns, persistedFormItem.gridFilterObject)
                    .addTemplate(CoreFormItemTemplates.DatasourceGridTemplate);
            } else if (snapshotField.type === SellerSnapshotFieldType.UIElement) {
                return new SellerSnapshotFormFieldProperties().createSimpleItem(snapshotField.fieldKey, snapshotField.caption, null, null, null, persistedFormItem.colSpan)
                    .addTemplate(snapshotField.template).setLabelVisible(false);
            } else {
                const formItem = this.createSimpleFormItem(snapshotField, persistedFormItem.colSpan);
                this.snapshotFormData[formItem.dataField] = formItem.editorOptions.value;
                return formItem;
            }
        } else if (persistedFormItem.itemType === CoreFormItemType.Group) {
            const items = [];
            if (persistedFormItem.items && persistedFormItem.items.length > 0) {
                persistedFormItem.items.forEach(childItem => items.push(this.getFullFormItem(childItem)));
            }
            const labelText = persistedFormItem.label?.['text'] ?? this.groupItemCaption;
            return new SellerSnapshotFormFieldProperties().createGroupItem(persistedFormItem.colSpan, persistedFormItem.colCount, labelText, items, this.helperService.generateRandomGuid());
        }
    }

    formItemsChanged(reconfigureGrids: boolean = true): void {
        if (reconfigureGrids) {
            // Update grids that may have changed
            this.formItems.forEach(item => {
                if (item.editorType === CoreInputEditorType.Grid) {
                    this.configureDatasourceGrid(item);
                } else if (item.items && item.items.length > 0) {
                    item.items.forEach(childItem => {
                        if (childItem.editorType === CoreInputEditorType.Grid) {
                            this.configureDatasourceGrid(childItem);
                        }
                    })
                }
            });
        }

        this.isSnapshotEditable = false;
        this.cleanedFormItems = this.cleanFormItems(this.formItems, this.formColCount);

        setTimeout(() => {
            this.snapshotForm?.instance.repaint();
        });
    }

    cleanFormItems(formItems: SellerSnapshotFormFieldProperties[], colCount: number): SellerSnapshotFormFieldProperties[] {
        const cleanedFormItems = [];
        let openCols = colCount;
        formItems.forEach(sourceItem => {
            // Horizontal lines should always be full-width
            if (sourceItem.dataField === this.horizontalLineFieldKey) {
                sourceItem.colSpan = colCount;
            }
            // Determine if next item does not fit and we need to complete row with an empty item
            const item = this.helperService.deepCopyTwoPointO(sourceItem);
            item.visible = true;

            const snapshotField = this.snapshotFields.find(x => x.fieldKey === item.dataField);
            if (item?.label && snapshotField && (snapshotField.type === SellerSnapshotFieldType.HistSellerProperty || snapshotField.type === SellerSnapshotFieldType.HistSellerAttribute
                || snapshotField.type === SellerSnapshotFieldType.HistSellerTextAttribute || snapshotField.type === SellerSnapshotFieldType.AccountFactor)) {
                item.label.template = this.labelTemplateName;
            }
            
            if (snapshotField && !(snapshotField.type === SellerSnapshotFieldType.Datasource || snapshotField.type === SellerSnapshotFieldType.UIElement)) {
                this.isSnapshotEditable = true;                
            }

            if (item.editorType === CoreInputEditorType.Grid) {
                const datasourceId = parseInt(item.dataField.replace('ds_', ''), 10);
                item.visible = this.permissionService.checkCurrentUserDatasourcePermission(datasourceId, CoreAccessLevel.View);
            }

            if (item.itemType === CoreFormItemType.Group && item.items && item.items.length > 0) {
                item.items = this.cleanFormItems(item.items, item.colCount);
            }

            if (item.visible) {
                if (item.colSpan <= colCount) {
                    if (item.colSpan > openCols) {
                        const emptyItem = new SellerSnapshotFormFieldProperties().createEmptyItem(openCols);
                        cleanedFormItems.push(emptyItem);
                        cleanedFormItems.push(item);
                        openCols = (colCount - item.colSpan) === 0 ? colCount : colCount - item.colSpan;
                    } else {
                        cleanedFormItems.push(item);
                        openCols = (openCols - item.colSpan) === 0 ? colCount : openCols - item.colSpan;
                    }
                } else {
                    cleanedFormItems.push(item);
                    openCols = colCount;
                }
            }
        });
        if (openCols < colCount) {
            const emptyItem = new SellerSnapshotFormFieldProperties().createEmptyItem(openCols);
            cleanedFormItems.push(emptyItem);
        }
        return cleanedFormItems;
    }

    getInputEditorType(snapshotField: SellerSnapshotField): CoreInputEditorType {
        switch (snapshotField.type) {
            case SellerSnapshotFieldType.SellerProperty:
                switch (snapshotField.fieldKey) {
                    case 'name':
                    case 'emailAddress':
                    case 'loginName':
                        return CoreInputEditorType.TextBox;
                    case 'descr':
                        return CoreInputEditorType.TextArea;
                    case 'userGroupId':
                        return CoreInputEditorType.Lookup;
                    case 'isPayee':
                        return CoreInputEditorType.CheckBox;
                }
                break;
            case SellerSnapshotFieldType.SellerImport:
            case SellerSnapshotFieldType.HistSellerTextAttribute:
                return CoreInputEditorType.TextBox;
            case SellerSnapshotFieldType.HistSellerProperty:
            case SellerSnapshotFieldType.HistSellerAttribute:
                return CoreInputEditorType.Lookup;
            case SellerSnapshotFieldType.AccountFactor:
                return CoreInputEditorType.NumberBox;
            case SellerSnapshotFieldType.Datasource:
                return CoreInputEditorType.Grid;
        }
    }

    getLookupDataset(snapshotField: SellerSnapshotField): any[] {
        switch (snapshotField.type) {
            case SellerSnapshotFieldType.HistSellerProperty:
                return this.sellerMappings;
            case SellerSnapshotFieldType.SellerProperty:
                if (snapshotField.fieldKey === 'userGroupId') {
                    return this.permissions;
                }
                return null;
            case SellerSnapshotFieldType.HistSellerAttribute:
                return this.attributeLookups[snapshotField.fieldKey.replace('attr_', '')];
        }
    }

    getValue(snapshotField: SellerSnapshotField, readOnlyRef: { readOnly: boolean }): any {
        if (this.seller && (this.histSeller || !this.isDateUsed)) {
            switch (snapshotField.type) {
                case SellerSnapshotFieldType.SellerProperty:
                case SellerSnapshotFieldType.SellerImport:
                    return this.seller[snapshotField.fieldKey];
                case SellerSnapshotFieldType.HistSellerTextAttribute:
                    return this.histSeller?.[snapshotField.fieldKey] ?? '';
                case SellerSnapshotFieldType.HistSellerAttribute:
                case SellerSnapshotFieldType.HistSellerProperty:
                    return this.histSeller?.[snapshotField.fieldKey];
                case SellerSnapshotFieldType.AccountFactor:
                    const variableId = parseInt(snapshotField.fieldKey.replace('var_', ''), 10);
                    const accountFactors = this.accountFactors
                        .filter(x => x.sellerId === this.seller.id && x.variableId === variableId && new Date(x.beginDate) <= this.date && (x.endDate === null || new Date(x.endDate) >= this.date));
                    readOnlyRef.readOnly = accountFactors.length > 1;
                    return accountFactors.length > 0 ? accountFactors.reduce((af1, af2) => af1 + af2.value, 0) : null;
            }
        } else {
            return null;
        }
    }

    getReadOnly(snapshotField: SellerSnapshotField, value: any): boolean {
        switch (snapshotField.type) {
            case SellerSnapshotFieldType.SellerProperty:
            case SellerSnapshotFieldType.SellerImport:
                if (snapshotField.fieldKey === 'loginName' || snapshotField.fieldKey === 'userGroupId') {
                    return !this.permissionEditSeller || !this.permissionEditAccountSecurity;
                } else {
                    return !this.permissionEditSeller;
                }
            case SellerSnapshotFieldType.HistSellerProperty:
            case SellerSnapshotFieldType.HistSellerAttribute:
            case SellerSnapshotFieldType.HistSellerTextAttribute:
                return !this.permissionEditAccountHistory;
            case SellerSnapshotFieldType.AccountFactor:
                return value === null ? !this.permissionAddFactor : !this.permissionEditFactor;
            case SellerSnapshotFieldType.Datasource:
                return false;
            default:
                return false;
        }
    }

    getItemGroup(snapshotField: SellerSnapshotField): SellerSnapshotFieldGroup {
        switch (snapshotField.type) {
            case SellerSnapshotFieldType.SellerProperty:
            case SellerSnapshotFieldType.SellerImport:
            case SellerSnapshotFieldType.HistSellerProperty:
                return SellerSnapshotFieldGroup.BasicInfo;
            case SellerSnapshotFieldType.HistSellerAttribute:
            case SellerSnapshotFieldType.HistSellerTextAttribute:
                return SellerSnapshotFieldGroup.HistSellerAttributes;
            case SellerSnapshotFieldType.AccountFactor:
                return SellerSnapshotFieldGroup.AccountFactors;
            case SellerSnapshotFieldType.Datasource:
                return SellerSnapshotFieldGroup.Datasources;
            case SellerSnapshotFieldType.UIElement:
                return SellerSnapshotFieldGroup.VisualElements;
        }
    }

    getGroupText(group: SellerSnapshotFieldGroup): string {
        switch (group) {
            case SellerSnapshotFieldGroup.BasicInfo:
                return 'Basic Info';
            case SellerSnapshotFieldGroup.HistSellerAttributes:
                return 'Attributes';
            case SellerSnapshotFieldGroup.AccountFactors:
                return 'Factors';
            case SellerSnapshotFieldGroup.Datasources:
                return 'Datasources';
            case SellerSnapshotFieldGroup.VisualElements:
                return 'Visual Elements';
        }
    }
    
    getCurrentHistSeller(): void {
        if (this.histSellers && this.histSellers.length > 0) {
            this.histSeller = this.histSellers.find(x => x.sellerId === this.seller.id && new Date(x.beginDate) <= this.date && (x.endDate === null || new Date(x.endDate) >= this.date));
        }
    }

    getEffectiveDateHintText(data: any): string {
        let beginDate, endDate;
        const snapshotField = this.snapshotFields.find(x => x.fieldKey === data.dataField);
        if (snapshotField.type === SellerSnapshotFieldType.HistSellerProperty || snapshotField.type === SellerSnapshotFieldType.HistSellerAttribute
            || snapshotField.type === SellerSnapshotFieldType.HistSellerTextAttribute) {
            beginDate = this.histSeller.beginDate;
            endDate = this.histSeller.endDate;
        } else if (snapshotField.type === SellerSnapshotFieldType.AccountFactor) {
            const variableId = parseInt(snapshotField.fieldKey.replace('var_', ''), 10);
            const accountFactors = this.accountFactors
                .filter(x => x.sellerId === this.seller.id && x.variableId === variableId && new Date(x.beginDate) <= this.date && (x.endDate === null || new Date(x.endDate) >= this.date));
            if (accountFactors.length === 1) {
                beginDate = accountFactors[0].beginDate;
                endDate = accountFactors[0].endDate;
            } else if (accountFactors.length === 0) {
                beginDate = Date.now();
                endDate = null;
            }
        }

        return beginDate ? (`Effective ${String.format('{0:d}', new Date(beginDate))}${(endDate ? ' - ' + String.format('{0:d}', new Date(endDate)) : '')}`) : '';
    }

    configureDatasourceGrid(formItem: SellerSnapshotFormFieldProperties): void {
        this.snapshotDatasources[formItem.dataField] = {
            props: new GridProps(`GRID_${formItem.dataField}`, null, false, true, false, false, false).addCssClass(`snapshot-grid-${(formItem.heightValue ?? 25)}`),
            columns: [],
            dataSource: []
        };

        // Get data
        if (formItem.gridColumns && formItem.gridColumns.length > 0) {
            const datasourceId = parseInt(formItem.dataField.replace('ds_', ''), 10);
            const context = new SellerSnapshotDatasourceContext(datasourceId, this.seller.id, this.date, formItem.gridFilterObject);
            this.sellerSnapshotService.getSnapshotDatasource(context).pipe(unwrapArray(this.toast)).subscribe(result => {
                this.snapshotDatasources[formItem.dataField].dataSource = result;
            });
        } else {
            this.snapshotDatasources[formItem.dataField].dataSource = [];
        }

        // Configure columns
        const coreColumns: CoreColumn[] = [];
        formItem.gridColumns?.forEach(column => {
            const coreColumn = this.getDatasourceCoreColumn(column);
            if (coreColumn) {
                coreColumns.push(coreColumn);
            }
        });
        this.snapshotDatasources[formItem.dataField].columns = coreColumns;
    }

    getDatasourceCoreColumn(column: string): CoreColumn {
        const fieldType = this.gridConfigPopup.getDatasourceFieldType(column);
        const columnHeader = this.columnMappings[column];
        const format = (value: any) => {
            const formatString = this.singletons.find(x => x.refName === column)?.format;
            return formatString ? String.format(`{0:${formatString}}`, value) : value;
        };
        switch (fieldType) {
            case SellerSnapshotDatasourceFieldType.Seller:
                return this.getLookupColumn(column, columnHeader, 'seller');
            case SellerSnapshotDatasourceFieldType.Tag:
                return this.getLookupColumn(column, columnHeader, column);
            case SellerSnapshotDatasourceFieldType.Qty:
                return new CoreColumn(column, columnHeader, true, 'number', null, true, format);
            case SellerSnapshotDatasourceFieldType.Date:
                return new CoreColumn(column, columnHeader, true, 'date', null, true, format);
            case SellerSnapshotDatasourceFieldType.Text:
                return new CoreColumn(column, column === 'notes' ? 'Notes' : columnHeader, true, 'string');
            case SellerSnapshotDatasourceFieldType.Customer:
                return this.getLookupColumn(column, columnHeader, 'customer');
            case SellerSnapshotDatasourceFieldType.Product:
                return this.getLookupColumn(column, columnHeader, 'product');
            case SellerSnapshotDatasourceFieldType.Unknown:
                return null;
        }
    }

    getLookupColumn(column: string, columnHeader: string, mappingKey: string): CoreColumn {
        const lookupData = Object.keys(this.masterMapping[mappingKey]).map(id => ({ id: parseInt(id, 10), name: this.masterMapping[mappingKey][id] }));
        return new CoreColumn(column, columnHeader, true, 'string', new ColumnType().createLookup(lookupData, 'id', 'name', null, false), true);
    }

    getCustomerGroupText(rowData: any): string {
        const customerId = parseInt(rowData['customer_id'], 10);
        const customerGroupId = parseInt(this.masterMapping['customerToGroup'][customerId], 10);
        return this.masterMapping['customerGroup'][customerGroupId];
    }

    getProductGroupText(rowData: any): string {
        const productId = parseInt(rowData['product_id'], 10);
        const productGroupId = parseInt(this.masterMapping['productToGroup'][productId], 10);
        return this.masterMapping['productGroup'][productGroupId];
    }

    onSnapshotValueChanged(args: any, e: any): void {
        this.snapshotFormDataChanges[args.dataField] = e.value;
        this.appElementsService.headerComponent.unsavedChanges = true;
    }

    submitForm(): void {
        const changesContext: SellerSnapshotChangesContext = {
            sellerId: this.seller.id,
            date: new Date(this.date.toDateString()),
            histSellerId: this.histSeller.id,
            fields: []
        };
        Object.keys(this.snapshotFormDataChanges).forEach(prop => {
            if (this.snapshotFormData[prop] !== this.snapshotFormDataChanges[prop]) {
                const field = this.helperService.deepCopyTwoPointO(this.snapshotFields.find(x => x.fieldKey === prop));
                field.value = this.snapshotFormDataChanges[prop].toString();
                changesContext.fields.push(field);
            }
        });

        this.sellerSnapshotService.updateSellerSnapshotFields(changesContext).subscribe(response => {
            if (response.responseCode === 1) {
                this.snapshotFormDataChanges = {};
                this.dataUpdated.emit();
            } else {
                this.updateFailed.emit(response);
            }
        });
    }

    editLayout(): void {
        if (this.editTabOnlyMode) {
            this.popupHeight = 190;
            this.minPopupHeight = 190;
        } else {
            this.popupHeight = 700;
            this.minPopupHeight = 500;
            this.popupGridHeight = this.popupHeight - this.popupGridHeightDiff;
        }
        this.editPopupVisible = true;
        this.configureEditPopupButtons();
    }

    configureEditPopupButtons(): void {
        this.editLayoutPopupSaveButtonOptions = {
            text: 'Save',
            disabled: (!this.sellerLayout.name || this.sellerLayout.name.length === 0),
            onClick: () => {
                if (!this.sellerLayout.isGridView) {
                    const layout = {
                        formColCount: this.formColCount,
                        formItems: []
                    };
                    this.formItems.forEach(item => {
                        if (item.itemType === CoreFormItemType.Simple) {                        
                            layout.formItems.push(this.persistSimpleItem(item));
                        } else if (item.itemType === CoreFormItemType.Group) {
                            const childPersistFormItems = [];
                            item.items.forEach(childItem => {
                                childPersistFormItems.push(this.persistSimpleItem(childItem));
                            });
                            layout.formItems.push({
                                label: item.label,
                                itemType: item.itemType,
                                dataField: item.dataField,
                                colSpan: item.colSpan,
                                colCount: item.colCount,
                                items: childPersistFormItems
                            });
                        }
                    });
                    this.sellerLayout.layout = JSON.stringify(layout);
                }
                
                if (this.sellerLayout.id > 0) {
                    // Update
                    this.sellerSnapshotService.updateSellerLayout(this.sellerLayout).pipe(unwrap(this.toast)).subscribe(result => {
                        this.layoutUpdated.emit(result.id);
                        this.toast.success(`Layout '${this.sellerLayout.name}' saved successfully`);
                        this.editPopupVisible = false;
                        this.formItemsChanged();
                    });
                } else {
                    // Insert
                    this.sellerLayout.id = 0;
                    this.sellerSnapshotService.insertSellerLayout(this.sellerLayout).pipe(unwrap(this.toast)).subscribe(result => {
                        this.layoutUpdated.emit(result.id);
                        this.toast.success(`Layout '${this.sellerLayout.name}' created successfully`)
                        this.editPopupVisible = false;
                        this.formItemsChanged();
                    });
                }
            }
        };

        this.editLayoutPopupCancelButtonOptions = {
            text: 'Cancel',
            onClick: () => {
                this.refreshSnapshot();
                this.editPopupVisible = false;
                if (this.sellerLayout.id === 0) {
                    this.layoutUpdated.emit(0);
                    this.formItems = [];
                    this.formItemsChanged();
                }
            }
        };

        this.editLayoutPopupDeleteButtonOptions = {
            text: 'Delete Layout',
            visible: !this.editTabOnlyMode && this.permissionDeleteAccountSnapshotLayouts,
            disabled: this.sellerLayout.id <= 0,
            elementAttr: {
                class: 'dx-button-danger'
            },
            onClick: () => {
                const confirmDeleteMessage = 'Are you sure you want to delete this layout?';
                confirm(confirmDeleteMessage, `Delete Layout ${this.sellerLayout.name}?`).then((dialogResult) => {
                    if (dialogResult) {
                        this.sellerSnapshotService.deleteSellerLayout(this.sellerLayout.id).subscribe(result => {
                            this.toast.success(`Layout ${this.sellerLayout.name} has been deleted successfully`);
                            this.editPopupVisible = false;
                            this.layoutUpdated.emit(0);
                        });
                    }
                });
            }
        };
    }

    onLayoutNameKeyDown(e: any): void {
        setTimeout(() => this.configureEditPopupButtons());
    }

    persistSimpleItem(item: SellerSnapshotFormFieldProperties): any {
        return {
            itemType: item.itemType,
            dataField: item.dataField,
            colSpan: item.colSpan,
            heightValue: item.heightValue ?? undefined,
            gridColumns: item.gridColumns ?? [],
            gridFilterObject: item.gridFilterObject ?? null
        };
    }

    onAddFormItem = (e: Parameters<DxDataGridTypes.RowDragging['onAdd']>[0]) => {
        if (e.itemData.itemType) {
            if (e.itemData.itemType === CoreFormItemType.Group) {
                this.toast.error('Cannot move a form group inside of another form group');
            } else {
                const fromIndex = e.fromData.findIndex(x => x.dataField === e.itemData.dataField);
                e.fromData.splice(fromIndex, 1);
                e.toData.splice(e.toIndex, 0, e.itemData);
            }
        } else if (e.itemData.type === SellerSnapshotFieldType.UIElement) {
            const formItem = new SellerSnapshotFormFieldProperties().createSimpleItem(e.itemData.fieldKey, e.itemData.caption, null, null, null)
                .addTemplate(e.itemData.template).setLabelVisible(false);            
            // Add it to the destination grid
            e.toData.splice(e.toIndex, 0, formItem);
        } else if (e.itemData.type === SellerSnapshotFieldType.Datasource) {
            const formItem = new SellerSnapshotFormFieldProperties().createSimpleItem(e.itemData.fieldKey, e.itemData.caption, null, null, CoreInputEditorType.Grid,
                e.itemData.colSpan, e.itemData.readOnly)
                .addTemplate(CoreFormItemTemplates.DatasourceGridTemplate).setLabelVisible(true);
            e.toData.splice(e.toIndex, 0, formItem);
        } else {
            const formItem = this.createSimpleFormItem(e.itemData, 1);
            // Update form value
            if (this.snapshotFormData) {
                this.snapshotFormData[e.itemData.fieldKey] = formItem.editorOptions.value;
            }
            // Hide it from the fields grid
            e.itemData.visible = false;
            // Add it to the destination grid
            e.toData.splice(e.toIndex, 0, formItem);            
        }

        this.formItemsChanged();
    };

    createSimpleFormItem(snapshotField: SellerSnapshotField, colSpan: number): SellerSnapshotFormFieldProperties {
        let editorType, dataset, value, readOnly;
        editorType = this.getInputEditorType(snapshotField);
        if (editorType === CoreInputEditorType.Lookup) {
            dataset = this.getLookupDataset(snapshotField);
        }
        
        let readOnlyRef = { readOnly: false };
        if (editorType !== CoreInputEditorType.Grid) {
            value = this.getValue(snapshotField, readOnlyRef);
        }
        readOnly = readOnlyRef.readOnly || this.getReadOnly(snapshotField, value);

        return new SellerSnapshotFormFieldProperties().createSimpleItem(snapshotField.fieldKey, snapshotField.caption, value, dataset, 
            editorType, colSpan, readOnly).addOnValueChangedFunction(this, this.onSnapshotValueChanged);
    }

    onRemoveFormItem = (e: Parameters<DxDataGridTypes.RowDragging['onRemove']>[0]) => {
        // Remove it from the form grid
        const fromIndex = e.fromData.findIndex(x => (e.itemData.dataField && x.dataField === e.itemData.dataField) 
            || (e.itemData.itemType === CoreFormItemType.Group && x.itemType === CoreFormItemType.Group &&  e.itemData.label.text === x.label.text));
        if (fromIndex > -1) {
            e.fromData.splice(fromIndex, 1);
        }

        // Redisplay in the fields grid
        setTimeout(() => this.snapshotFields.forEach(field => field.visible = !this.formItems.some(x => x.dataField === field.fieldKey || x.items?.some(y => y.dataField === field.fieldKey))
            || field.type === SellerSnapshotFieldType.UIElement));

        this.formItemsChanged();
        
    };

    onReorderFormItem = (e: DxDataGridTypes.RowDraggingReorderEvent) => {
        const fromIndex = e.fromData.findIndex(x => x.dataField === e.itemData.dataField);
        e.toData.splice(fromIndex, 1);
        e.toData.splice(e.toIndex, 0, e.itemData);
        this.formItemsChanged();
    }

    onFormGridToolbarPreparing(e: any): void {
        e.toolbarOptions.items[0].options.hint = 'Add a group';
        e.toolbarOptions.items[0].options.text = 'Add a group';
        e.toolbarOptions.items[0].showText = 'always';

        e.toolbarOptions.items.unshift({
            location: 'before',
            widget: 'dxNumberBox',
            name: 'formColCount',
            options: {
                label: 'Columns',
                labelLocation: 'left',
                width: 100,
                showSpinButtons: true,
                value: this.formColCount,
                onValueChanged: (e) => {
                    this.formColCount = e.value;
                    this.formItemsChanged();
                }
            }
        });
    }

    onFormGridSaved(e: any): void {
        this.formItemsChanged();
    }

    onFormGridRowExpanding(e: any): void {
        if (e.key.itemType === CoreFormItemType.Simple) {
            e.cancel = true;
        }
    }

    onFormGridInitNewRow(e: any): void {
        e.data.itemType = CoreFormItemType.Group;
        e.data.label = { text: this.groupItemCaption, visible: true };
        e.data.colSpan = 1;
        e.data.colCount = 1;
        e.data.items = [];
    }

    customizeFormItem = (item: any) => {
        // Only show the Column Count field for group items
        if (item && item.dataField === 'colCount') {
            item.visible = this.getEditingFormItemCellValue('itemType') === CoreFormItemType.Group;
        }

        // Only allow label-editing for group types
        if (item && item.dataField === 'label.text') {
            item.editorOptions.readOnly = this.getEditingFormItemCellValue('itemType') !== CoreFormItemType.Group;
        }
    }

    getEditingFormItemCellValue(column: string): string {
        const gridInstance = this.formGrid.instance;
        const editRowKey = gridInstance.option('editing.editRowKey');
        const rowIndex = gridInstance.getRowIndexByKey(editRowKey);
        return gridInstance.cellValue(rowIndex, column);
    }
    
    isEditButtonVisible = (e) => {
        return e.row.data.dataField !== this.horizontalLineFieldKey && e.row.data.editorType !== CoreInputEditorType.Grid;
    }
    
    isDeleteButtonVisible = (e) => {
        return e.row.data.itemType === CoreFormItemType.Group;
    }

    isGridConfigButtonVisible = (e) => {
        return e.row.data.editorType === CoreInputEditorType.Grid;
    }

    openNewLayoutPopup(): void {
        this.formItems = [];
        this.formItemsChanged();
        this.snapshotFields.forEach(x => x.visible = true);
        setTimeout(() => this.editLayout());
    }

    onResizePopup(e: any): void {
        const popupHeight = Math.max(this.minPopupHeight, e.height);
        this.popupGridHeight = popupHeight - this.popupGridHeightDiff;
    }

    isSnapshotDateUsed(): boolean {
        let isDateUsed = false;
        this.formItems?.forEach(item => {
            if (this.doesItemUseSnapshotDate(item)) {
                isDateUsed = true;
            }
        });
        this.isDateUsed = isDateUsed;
        return isDateUsed;
    }

    doesItemUseSnapshotDate(item: SellerSnapshotFormFieldProperties): boolean {        
        let isDateUsed = false;
        if (item.itemType === CoreFormItemType.Group) {
            item.items?.forEach(childItem => {
                if (this.doesItemUseSnapshotDate(childItem)) {
                    isDateUsed = true;
                }
            });
        } else {
            const snapshotField = this.snapshotFields.find(x => x.fieldKey === item.dataField)?.type;            
            switch (snapshotField) {
                case SellerSnapshotFieldType.AccountFactor:
                case SellerSnapshotFieldType.HistSellerProperty:
                case SellerSnapshotFieldType.HistSellerAttribute:
                case SellerSnapshotFieldType.HistSellerTextAttribute:
                    isDateUsed = true;
                    break;
                case SellerSnapshotFieldType.Datasource:
                    if (item.gridFilterObject && Array.isArray(item.gridFilterObject) && item.gridFilterObject.length === 3 
                        && item.gridFilterObject[2] === this.gridConfigPopup.snapshotDateFieldName) {
                        isDateUsed = true;
                    } else if (item.gridFilterObject && item.gridFilterObject.length > 0) {
                        item.gridFilterObject.forEach(x => {
                            if (Array.isArray(x) && x.length >= 3 && x[2] === this.gridConfigPopup.snapshotDateFieldName) {
                                isDateUsed = true;
                            }
                        });
                    }
                    break;
            }
        }
        return isDateUsed;
    }
}
